import { Controller } from 'stimulus'
import SimpleroPusher from '~/common/helpers/simplero_pusher'
import { enter } from 'el-transition'
import { hasSourceDrag, hasFileDrag, getSourceDragData } from './source_dnd.js'
import { attachSourceToComposer } from './source_dnd_actions.js'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import { matchesObject, objectWriteTimestamp } from './object_panel_context'
import './conversation_component.css'

// How close to the top (px) before we start pulling older messages.
// Matches member chat (ENG-30134): large enough for touch fling, not so large
// that mid-thread scrolling pages eagerly.
const LOAD_MORE_THRESHOLD = 400

export default class extends Controller {
  static targets = ['submitButton', 'newMessage', 'objectPill', 'pageLink']

  // Load-more cursor lives as Stimulus values (member-chat contract, ENG-30134) —
  // never a hidden loader link whose href has to survive DOM replaces. The server
  // seeds them in the ERB and advances both in every load_more response.
  static values = {
    conversationId: String,
    object: Object,
    attachments: { type: Boolean, default: true },
    sharedChannel: String,
    // Oldest message id currently rendered; the next page is id < this.
    nextBefore: { type: Number, default: 0 },
    hasMore: { type: Boolean, default: false },
  }

  connect() {
    this.element.addEventListener('dragover', this.#handleDragOver)
    this.element.addEventListener('dragleave', this.#handleDragLeave)
    this.element.addEventListener('drop', this.#handleDrop)

    window.addEventListener('message', this.parentReady)
    this.sendObjectContext()
    if (this.hasSharedChannelValue) {
      this.sharedBindings.forEach(([event, callback]) =>
        SimpleroPusher.bind(this.sharedChannelValue, event, callback)
      )
      this.element.addEventListener('focusout', this.retryDeferredSync)
      document.addEventListener('visibilitychange', this.sharedPageShown)
    }

    this.loadingMessages = false
    this.noMoreMessages = false
    this.#seedCursorFromRenderedMessages()
    this.#initializePaginator()
    document.addEventListener('coach:piece-loaded', this.#onPieceLoaded)
  }

  disconnect() {
    document.removeEventListener('coach:piece-loaded', this.#onPieceLoaded)
    window.removeEventListener('message', this.parentReady)
    document.removeEventListener('visibilitychange', this.sharedPageShown)
    this.element.removeEventListener('focusout', this.retryDeferredSync)
    if (this.hasSharedChannelValue)
      this.sharedBindings.forEach(([event, callback]) =>
        SimpleroPusher.unbind(this.sharedChannelValue, event, callback)
      )
    this.sharedSyncAbort?.abort()
    clearTimeout(this.sharedRetryTimer)
    this.element.removeEventListener('dragover', this.#handleDragOver)
    this.element.removeEventListener('dragleave', this.#handleDragLeave)
    this.element.removeEventListener('drop', this.#handleDrop)
    this.#hideDropZone()

    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf)
      this.scrollRaf = null
    }

    if (this.boundMaybeLoadMore && this.messagesScrollContainer) {
      this.messagesScrollContainer.removeEventListener(
        'scroll',
        this.boundMaybeLoadMore
      )
    }
  }

  parentReady = (event) => {
    if (
      window.parent === window ||
      event.source !== window.parent ||
      event.origin !== window.location.origin
    )
      return
    if (event.data?.purpose === 'coach_object_ready') this.sendObjectContext()
    if (
      event.data?.type === 'setTheme' &&
      ['light', 'dark'].includes(event.data.theme)
    ) {
      document.documentElement.dataset.parentTheme = event.data.theme
      for (const element of [document.documentElement, document.body])
        element.classList.toggle('dark', event.data.theme === 'dark')
    }
    if (event.data?.purpose === 'coach_page_context') {
      this.linkedToPage =
        this.hasObjectValue &&
        matchesObject(this.objectValue, event.data.object)
      this.updateObjectLink()
    }
  }

  objectPillTargetConnected() {
    this.updateObjectLink()
  }
  pageLinkTargetConnected() {
    this.updateObjectLink()
  }

  updateObjectLink() {
    this.objectPillTargets.forEach((element) =>
      element.classList.toggle('hidden', Boolean(this.linkedToPage))
    )
    this.pageLinkTargets.forEach((element) =>
      element.classList.toggle('hidden', !this.linkedToPage)
    )
  }

  sendObjectContext() {
    if (window.parent === window) return
    window.parent.postMessage(
      { purpose: 'request_theme' },
      window.location.origin
    )
    window.parent.postMessage(
      {
        purpose: 'coach_object_attached',
        ...(this.hasObjectValue ? this.objectValue : {}),
      },
      window.location.origin
    )
    if (!this.hasObjectValue) return
    // The iframe can finish loading before the panel controller. Replay the last
    // durable write when the parent announces readiness so that race loses nothing.
    const writes = [
      ...this.element.querySelectorAll(
        '[data-chat-bot--message-object-write-value]'
      ),
    ]
      .map((message) => {
        try {
          return {
            message_id: message.dataset.messageId,
            ...JSON.parse(
              message.getAttribute('data-chat-bot--message-object-write-value')
            ),
          }
        } catch {
          return null
        }
      })
      .filter((write) => objectWriteTimestamp(write?.at))
      .sort((a, b) =>
        objectWriteTimestamp(a.at).localeCompare(objectWriteTimestamp(b.at))
      )
    if (writes.length)
      window.parent.postMessage(
        { purpose: 'coach_object_write', ...writes.at(-1) },
        window.location.origin
      )
  }

  get sharedBindings() {
    return [
      ['pusher:subscription_succeeded', this.syncSharedMessages],
      ['message_created', this.sharedMessageCreated],
      ['conversation_changed', this.syncSharedMessages],
    ]
  }

  // The transcript still loading as a page piece: a sync now would draw it all a second time.
  // #onPieceLoaded runs the held sync once it lands, with what it shows.
  get messagesPiecePending() {
    return Boolean(
      this.element.querySelector(
        '.js-chat-bot-messages > [data-coach-piece="messages"]'
      )
    )
  }

  get inlineEditOpen() {
    return Boolean(
      this.element.querySelector(
        '.js-chat-bot-messages form, .chat-bot-conversation-header .inline-editor-editing'
      )
    )
  }

  retryDeferredSync = () => {
    setTimeout(() => {
      if (this.sharedSyncPending && this.element.isConnected)
        this.syncSharedMessages()
    }, 0)
  }

  sharedMessageCreated = (data) => {
    this.sharedMessageQueue = (
      this.sharedMessageQueue || Promise.resolve()
    ).then(() => this.fetchSharedMessages(data))
    return this.sharedMessageQueue
  }

  async fetchSharedMessages(data) {
    if (!this.element.isConnected) return
    if (document.hidden)
      this.sharedHiddenIds = [
        ...new Set([
          ...(this.sharedHiddenIds || []),
          ...(data.message_ids || []),
        ]),
      ]
    if (this.inlineEditOpen || this.syncingShared || document.hidden) {
      this.sharedSyncPending = true
      return
    }
    const generation = this.sharedGeneration || 0
    for (const id of data.message_ids || []) {
      const url = new URL(
        `/chat_bot/conversations/${this.conversationIdValue}/messages/${id}`,
        window.location.origin
      )
      url.searchParams.set('append_if_missing', '1')
      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) {
          this.syncSharedMessages()
          continue
        }
        const payload = await response.json()
        if (!this.element.isConnected) return
        if (generation !== (this.sharedGeneration || 0)) {
          this.syncSharedMessages()
          return
        }
        if (this.inlineEditOpen) {
          this.sharedSyncPending = true
          return
        }
        AjaxHelper.doUpdate(payload)
        // Events can cross in flight. Leave ordered nodes mounted so their streams stay subscribed.
        if (!Array.isArray(payload.active_message_ids)) continue
        const container = this.element.querySelector('.js-chat-bot-messages')
        const activeIds = new Set(
          (payload.active_message_ids || []).map(String)
        )
        const mountedMessages = new Map()
        for (const child of [...container.children]) {
          if (!child.dataset.messageId) continue
          if (activeIds.has(child.dataset.messageId))
            mountedMessages.set(child.dataset.messageId, child)
          else child.remove()
        }
        let nextMessage = mountedMessages.values().next().value || null
        for (const messageId of payload.active_message_ids) {
          const message = mountedMessages.get(String(messageId))
          if (!message) continue
          if (message !== nextMessage)
            container.insertBefore(message, nextMessage)
          nextMessage = message.nextElementSibling
          while (nextMessage && !nextMessage.dataset.messageId)
            nextMessage = nextMessage.nextElementSibling
        }
      } catch (_error) {
        this.sharedSyncPending = true
        this.#scheduleSharedRetry()
      }
    }
  }

  // A hidden copy only notes the messages it hears about. Back in front, it fetches those first,
  // so the catch-up sync finds them shown and answers unchanged instead of drawing it all again.
  // Many missed messages are left to the sync alone.
  sharedPageShown = async () => {
    if (document.hidden) return
    const ids = this.sharedHiddenIds || []
    this.sharedHiddenIds = []
    if (ids.length && ids.length <= 20)
      await this.sharedMessageCreated({ message_ids: ids })
    this.syncSharedMessages()
  }

  syncSharedMessages = async () => {
    this.sharedSyncPending = true
    if (
      this.syncingShared ||
      document.hidden ||
      this.inlineEditOpen ||
      this.messagesPiecePending ||
      !this.element.isConnected
    )
      return
    this.sharedSyncPending = false
    this.syncingShared = true
    clearTimeout(this.sharedRetryTimer)
    this.sharedGeneration = (this.sharedGeneration || 0) + 1
    const abort = (this.sharedSyncAbort = new AbortController())
    const url = new URL(
      `/chat_bot/conversations/${this.conversationIdValue}/sync`,
      window.location.origin
    )
    const oldest = this.element.querySelector(
      '.js-chat-bot-messages > [data-message-id]'
    )
    if (oldest) {
      url.searchParams.set('oldest_message_id', oldest.dataset.messageId)
      // What this copy shows, so the server can say nothing changed instead of drawing it all
      // again. Not for a very long scrollback: the URL would get too long.
      const shown = this.element.querySelectorAll(
        '.js-chat-bot-messages > [data-message-id]'
      )
      if (shown.length <= 200)
        url.searchParams.set(
          'rendered',
          [...shown]
            .map((node) => `${node.dataset.messageId}:${node.dataset.state}`)
            .join(',')
        )
    }
    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: abort.signal,
      })
      if (!this.element.isConnected) return
      if ([401, 403, 404].includes(response.status)) {
        this.sharedBindings.forEach(([event, callback]) =>
          SimpleroPusher.unbind(this.sharedChannelValue, event, callback)
        )
        this.element.textContent =
          'You no longer have access to this conversation.'
        return
      }
      if (!response.ok) {
        this.#scheduleSharedRetry()
        return
      }
      const data = await response.json()
      if (!this.element.isConnected) return
      if (this.inlineEditOpen) {
        this.sharedSyncPending = true
        return
      }
      if (data.unchanged) {
        if (data.replace) AjaxHelper.doUpdate(data)
        this.sharedRetryDelay = 0
        return
      }
      const container = this.messagesScrollContainer
      const bottom =
        container.scrollHeight - container.clientHeight - container.scrollTop <
        40
      const scrollTop = container.scrollTop
      AjaxHelper.doUpdate(data)
      this.nextBeforeValue = Number(data.next_before) || 0
      this.hasMoreValue = Boolean(data.has_more)
      this.noMoreMessages = !this.hasMoreValue
      this.lastLoadedUrl = null
      container.scrollTop = bottom ? container.scrollHeight : scrollTop
      this.sharedRetryDelay = 0
    } catch (_error) {
      // Disconnect aborts the request; anything else is transient and retries on its own.
      if (!abort.signal.aborted) this.#scheduleSharedRetry()
    } finally {
      this.syncingShared = false
      if (this.sharedSyncPending && !this.inlineEditOpen)
        this.syncSharedMessages()
    }
  }

  // A failed catch-up has no event left to trigger another, so back off (2s → 30s) and retry.
  // Separate from sharedSyncPending, which the finally block re-runs immediately.
  #scheduleSharedRetry() {
    if (!this.element.isConnected) return
    clearTimeout(this.sharedRetryTimer)
    this.sharedRetryDelay = Math.min((this.sharedRetryDelay || 1000) * 2, 30000)
    this.sharedRetryTimer = setTimeout(
      this.syncSharedMessages,
      this.sharedRetryDelay
    )
  }

  // On the Coach page the messages arrive as a page piece (see
  // ConversationsComponent#coach_page_loader_script), usually after connect. Start
  // paging and the object context over once they land in this conversation.
  #onPieceLoaded = (event) => {
    if (event.detail?.name !== 'messages') return
    // A piece of a conversation the user already switched away from.
    if (!this.element.contains(event.target)) return
    this.loadingMessages = false
    this.noMoreMessages = false
    this.#seedCursorFromRenderedMessages()
    this.#initializePaginator()
    this.sendObjectContext()
    if (this.sharedSyncPending) this.syncSharedMessages()
  }

  // Deferred messages come without the load-more cursor the server seeds inline, and
  // may land before or after connect: seed it from the rendered window the same way.
  #seedCursorFromRenderedMessages() {
    if (this.hasMoreValue) return
    const oldest = this.element.querySelector(
      '.js-chat-bot-messages > [data-message-id]'
    )
    const more = this.element.querySelector(
      '.js-chat-bot-messages > .more_loader'
    )
    if (!oldest || !more) return
    this.nextBeforeValue = Number(oldest.dataset.messageId)
    this.hasMoreValue = true
  }

  // Pull older history when the user scrolls near the top of a long thread.
  // Same pattern as member chat: scroll listener (not IntersectionObserver) so
  // we keep loading while parked at the top after a short batch.
  #initializePaginator() {
    const container = this.messagesScrollContainer
    if (!container) return

    if (this.boundMaybeLoadMore) {
      container.removeEventListener('scroll', this.boundMaybeLoadMore)
    }

    this.boundMaybeLoadMore = () => {
      if (this.scrollRaf) return

      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null
        this.#maybeLoadMore()
      })
    }

    container.addEventListener('scroll', this.boundMaybeLoadMore, {
      passive: true,
    })

    // Scroll-then-init (member chat hit this same bug, ENG-30134): sticky-scroll —
    // a descendant controller that pins the container to the bottom — connects after
    // we do, so an immediate check would read scrollTop 0 and fire a spurious load on
    // every open/switch. Wait for the pin first, and then only auto-fill when the
    // first batch doesn't fill the container (a taller-than-viewport batch would never
    // fire a scroll event on its own). A scrollable container sitting at the top —
    // pinned long threads, and search style which never pins — waits for a real
    // scroll, or paging on open would render 80 messages where the window meant 40.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          container.clientHeight > 0 &&
          container.scrollHeight <= container.clientHeight
        ) {
          this.#maybeLoadMore()
        }
      })
    })
  }

  get messagesScrollContainer() {
    return this.element.querySelector('.js-chat-bot-messages-container')
  }

  // The a11y "Load earlier messages" button — same load as the scroll trigger,
  // minus the near-top threshold (focusing the hidden button doesn't guarantee
  // the container scrolled near the top).
  loadEarlierMessages(event) {
    event.preventDefault()
    if (this.loadingMessages || this.noMoreMessages) return

    this.#maybeLoadMore({ force: true })
  }

  #maybeLoadMore({ force = false } = {}) {
    if (this.loadingMessages || this.noMoreMessages) return

    const container = this.messagesScrollContainer
    if (!container) return
    if (!force && container.scrollTop > LOAD_MORE_THRESHOLD) return

    const url = this.#nextPageUrl()
    if (!url) {
      this.noMoreMessages = true
      return
    }

    this.#loadMore(url)
  }

  #nextPageUrl() {
    if (!this.hasMoreValue || !this.nextBeforeValue) return null

    return `/chat_bot/conversations/${this.conversationIdValue}/load_more?before=${this.nextBeforeValue}`
  }

  async #loadMore(url) {
    if (url === this.lastLoadedUrl) {
      this.noMoreMessages = true
      return
    }

    this.loadingMessages = true
    this.lastLoadedUrl = url

    const container = this.messagesScrollContainer

    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        throw new Error(`load_more failed: ${response.status}`)
      }

      const data = await response.json()
      const oldHeight = container.scrollHeight
      const oldScrollTop = container.scrollTop

      // Focus recovery: removing the loader (history exhausted) can destroy the
      // focused "Load earlier messages" button — the very element that triggered
      // this fetch — dropping keyboard users onto <body>. Remember the focus, then
      // move it to the first message of the fresh batch so reading continues there.
      const activeElement = document.activeElement
      const loaderId = data.insert_after && Object.keys(data.insert_after)[0]

      // insert_after must run before remove: AjaxHelper.doUpdate processes `remove`
      // first, which would delete the loader before messages can be inserted.
      if (data.insert_after) {
        AjaxHelper.doUpdate({ insert_after: data.insert_after })
      }
      // The batch lands right after the loader; grab its first node while the
      // loader is still in the DOM to have a focus target after it goes away.
      const firstInserted =
        loaderId && document.getElementById(loaderId)?.nextElementSibling
      if (data.replace || data.remove) {
        AjaxHelper.doUpdate({
          replace: data.replace,
          remove: data.remove,
        })
      }

      if (
        activeElement &&
        !activeElement.isConnected &&
        firstInserted?.isConnected
      ) {
        firstInserted.setAttribute('tabindex', '-1')
        firstInserted.focus({ preventScroll: true })
      }

      // Advance the cursor from the server contract (nil/0 next_before with
      // has_more false means the thread is fully drawn).
      this.nextBeforeValue = Number(data.next_before) || 0
      this.hasMoreValue = Boolean(data.has_more)

      requestAnimationFrame(() => {
        const delta = container.scrollHeight - oldHeight
        container.scrollTop = oldScrollTop + delta
        this.loadingMessages = false

        if (!this.#nextPageUrl()) {
          this.noMoreMessages = true
          return
        }

        if (delta <= 0) return

        this.#maybeLoadMore()
      })
    } catch (_error) {
      this.lastLoadedUrl = null
      this.loadingMessages = false
    }
  }

  submit() {
    this.submitButtonTarget.click()
  }

  scrollToTop(event) {
    if (
      event?.target?.closest(
        'a, button, input, textarea, select, [contenteditable]'
      )
    )
      return

    const container = this.element.querySelector(
      '.js-chat-bot-messages-container'
    )
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  allowNewMessage() {
    if (
      this.hasNewMessageTarget &&
      this.newMessageTarget.classList.contains('hidden')
    ) {
      enter(this.newMessageTarget)
    }
    // Don't steal focus while a modal is open (e.g. the feedback popup that
    // opens right as the reply completes).
    if (!this.#modalOpen())
      this.newMessageTarget
        ?.querySelector('.js-tiptap-editor-container')
        ?.dispatchEvent(new CustomEvent('editor:focus'))

    // The reply finished — send the next queued message, if any.
    this.#messageQueueController?.advance()
  }

  #modalOpen() {
    return !!document.querySelector('[data-controller~="modal"].in')
  }

  // Drag & drop: files → upload as sources; source cards → attach to composer
  #handleDragOver = (e) => {
    if (!this.attachmentsValue) return
    const isSource = hasSourceDrag(e.dataTransfer)
    const isFile = hasFileDrag(e.dataTransfer)
    if (!isSource && !isFile) return

    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    this.#showDropZone(isSource ? 'source' : 'file')
  }

  #handleDragLeave = (e) => {
    if (e.relatedTarget && this.element.contains(e.relatedTarget)) return
    this.#hideDropZone()
  }

  #handleDrop = (e) => {
    if (!this.attachmentsValue) return
    this.#hideDropZone()

    if (hasSourceDrag(e.dataTransfer)) {
      e.preventDefault()
      const payload = getSourceDragData(e.dataTransfer)
      if (payload) attachSourceToComposer(payload, { force: true })
      return
    }

    const files = Array.from(e.dataTransfer?.files || [])
    if (!files.length) return

    e.preventDefault()

    const inputController = this.#messageInputController
    if (inputController) inputController.addDroppedFiles(files)
  }

  get #messageInputController() {
    const el = this.element.querySelector(
      '[data-controller*="chat-bot--message-input"]'
    )
    return (
      el &&
      this.application.getControllerForElementAndIdentifier(
        el,
        'chat-bot--message-input'
      )
    )
  }

  get #messageQueueController() {
    const el = this.element.querySelector(
      '[data-controller~="chat-bot--message-queue"]'
    )
    return (
      el &&
      this.application.getControllerForElementAndIdentifier(
        el,
        'chat-bot--message-queue'
      )
    )
  }

  #showDropZone(kind = 'file') {
    if (this.dropZone) return

    const label =
      kind === 'source' ? 'Drop source to attach' : 'Drop files here'

    this.dropZone = document.createElement('div')
    this.dropZone.className =
      'absolute inset-0 z-10 flex items-center justify-center bg-sg-primary/10 border-2 border-dashed border-sg-primary rounded-lg pointer-events-none'
    const span = document.createElement('span')
    span.className = 'text-sg-primary font-medium text-lg'
    span.textContent = label
    this.dropZone.appendChild(span)
    this.element.classList.add('relative')
    this.element.appendChild(this.dropZone)
  }

  #hideDropZone() {
    this.dropZone?.remove()
    this.dropZone = null
  }
}
